import {useState} from "react";
import _ from "lodash";
import {useAtom, useAtomValue} from "jotai";
import {Loadable} from "jotai/vanilla/utils/loadable";
import {Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import MoreIcon from "@mui/icons-material/MoreVertOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {ResourceException} from "../../types/resourceError";
import {activeFarmLoadable} from "../../store/activeFarmStore";
import {Farm} from "../../types/domain/farm";
import {routerCompanyIdAtom} from "../../store/routerStore";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {useFarms} from "../../hooks/farmsHooks";
import {FarmIcon} from "../icons/FarmIcon";
import {EditIcon} from "../icons/EditIcon";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useTranslation} from "react-i18next";
import {activeYearAtom} from "../../store/activeYearAtom";

export const FarmHeader: React.FC = () => {
    const {t} = useTranslation();
    const companyId = useAtomValue(routerCompanyIdAtom);
    const farmState = useAtomValue(activeFarmLoadable);
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const farm = farmState.state === "hasData" && farmState.data !== undefined ? farmState.data : undefined;
    const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(null);
    const [farmEditDialogOpened, setFarmEditDialogOpened] = useState<boolean>(false);
    const farms = useFarms(companyId ?? null);
    const [activeYear] = useAtom(activeYearAtom);

    const onFarmEditDialogClose = (isSuccessful: boolean) => {
        setFarmEditDialogOpened(false);
        if (isSuccessful) {
            farms.refresh();
        }
        setOptionsAnchorEl(null);
    };

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap="1.125rem">
                {farm && (
                    <FarmIcon
                        sx={{
                            width: "1.75rem",
                            height: "1.75rem",
                            stroke: "#5161C1",
                            strokeWidth: "2.2",
                        }}
                    />
                )}
                {farmState.state === "hasError" && (
                    <ErrorIcon
                        sx={{
                            height: 36,
                            width: 36,
                        }}
                        color="error"
                    />
                )}
                <Typography fontSize={30} lineHeight={1.27} fontWeight={700}>
                    {renderTitle(farmState, activeYear)}
                </Typography>
            </Stack>
            {access.canEditCompanyFarms && (
                <Stack direction="row" alignItems="center" gap="0.5rem">
                    <Button
                        startIcon={<MoreIcon />}
                        size="large"
                        onClick={(e) => setOptionsAnchorEl(e.currentTarget)}
                        endIcon={<ArrowDownIcon />}
                    >
                        {t("insights.farm_header.options")}
                    </Button>
                    <Menu
                        id="farm-options-menu"
                        anchorEl={optionsAnchorEl}
                        open={!!optionsAnchorEl}
                        onClose={() => setOptionsAnchorEl(null)}
                    >
                        <MenuItem onClick={() => setFarmEditDialogOpened(true)}>
                            <ListItemIcon>
                                <EditIcon
                                    sx={{
                                        stroke: "#8F94A8",
                                        width: "1.125rem",
                                        height: "1.125rem",
                                    }}
                                />
                            </ListItemIcon>
                            {t("insights.farm_header.edit")}
                        </MenuItem>
                    </Menu>
                </Stack>
            )}

            {farmEditDialogOpened && companyId && (
                <FarmCreateDialog
                    mode="edit"
                    companyId={companyId}
                    selectedFarm={farm}
                    open={farmEditDialogOpened}
                    onClose={onFarmEditDialogClose}
                />
            )}
        </Stack>
    );
};

const renderTitle = (
    farmState: Loadable<Promise<Farm | undefined>>,
    activeYear: number | undefined,
): React.ReactNode => {
    if (activeYear) return activeYear.toString();

    if (farmState.state === "hasData" && farmState.data) {
        return farmState.data.name;
    } else if (farmState.state === "hasError") {
        return (farmState.error as ResourceException).resourceError.message;
    } else {
        return null;
    }
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canEditCompanyFarms"]);
